import { create } from "zustand";

import { TOUR_STEPS, type TourStepId } from "@optidev/shared-types";

/**
 * Anchor registry for the editor tour.
 *
 * The three controls the tour explains live in three unrelated components
 * (AgentChat's composer, the preview wrapper in IntegratedEditorWithAgent, and
 * PublishDropdown's trigger), so there is no shared parent to hang refs off.
 * Rather than thread refs through those trees, each host tags its element with
 * `data-tour-anchor` and this store resolves them from the DOM on demand.
 *
 * Resolving lazily also means a missing anchor is a normal outcome rather than a
 * crash: on mobile the preview lives behind a tab and simply is not mounted, and
 * the tour skips that step instead of pointing at nothing.
 */

export const TOUR_ANCHOR_ATTR = "data-tour-anchor";

interface TourState {
  /** Zero based index of the visible step. */
  step: number;
  /** True once the server says this user should see the tour. */
  isActive: boolean;

  setStep: (step: number) => void;
  activate: (step: number) => void;
  deactivate: () => void;
}

export const useTourStore = create<TourState>((set) => ({
  step: 0,
  isActive: false,

  setStep: (step) => set({ step }),
  activate: (step) => set({ isActive: true, step }),
  deactivate: () => set({ isActive: false }),
}));

/**
 * Find the element a step points at.
 *
 * Two components can render a Publish button (the integrated editor and the
 * power editor header) and only one is mounted at a time, but guard anyway by
 * preferring an element that actually occupies space.
 */
export function resolveTourAnchor(id: TourStepId): HTMLElement | null {
  const matches = Array.from(
    document.querySelectorAll<HTMLElement>(`[${TOUR_ANCHOR_ATTR}="${id}"]`),
  );

  return (
    matches.find((el) => el.getBoundingClientRect().width > 0) ??
    matches[0] ??
    null
  );
}

/** Step id for an index, clamped so callers never index out of range. */
export function tourStepIdAt(index: number): TourStepId {
  return TOUR_STEPS[Math.min(Math.max(index, 0), TOUR_STEPS.length - 1)];
}
